import {
  type NonZeroVector,
  type Point,
  moveHitArea,
  point,
} from '../../elements';
import type { HitArea } from '../../elements/HitArea';
import type { NodeType } from '../constants';
import type { NodeData } from '../types';
import type { NodeTypeIcon } from './NodeTypeIcon';
import { aciLeafIcon } from './types/aciLeaf';
import { aciSpineIcon } from './types/aciSpine';
import { apIcon } from './types/ap';
import { apicIcon } from './types/apic';
import { awsAlbIcon } from './types/awsAlb';
import { awsCneIcon } from './types/awsCne';
import { awsDxgwIcon } from './types/awsDxgw';
import { awsEniIcon } from './types/awsEni';
import { awsIgwIcon } from './types/awsIgw';
import { awsInstanceIcon } from './types/awsInstance';
import { awsNatGwIcon } from './types/awsNatGw';
import { awsNlbIcon } from './types/awsNlb';
import { awsSubnetIcon } from './types/awsSubnet';
import { awsTgwIcon } from './types/awsTgw';
import { awsVgwIcon } from './types/awsVgw';
import { awsVpcIcon } from './types/awsVpc';
import { awsVpcPeeringIcon } from './types/awsVpcPeering';
import { azureAppGwIcon } from './types/azureAppGw';
import { azureErcIcon } from './types/azureErc';
import { azureFwIcon } from './types/azureFw';
import { azureGwIcon } from './types/azureGw';
import { azureInstanceIcon } from './types/azureInstance';
import { azureLbIcon } from './types/azureLb';
import { azureNatIcon } from './types/azureNat';
import { azureNicIcon } from './types/azureNic';
import { azureSubnetIcon } from './types/azureSubnet';
import { azureVhubIcon } from './types/azureVhub';
import { azureVnetIcon } from './types/azureVnet';
import { azureVnetPeeringIcon } from './types/azureVnetPeering';
import { cloudIcon } from './types/cloud';
import { cloudInstanceIcon } from './types/cloudInstance';
import { cloudInternetGwIcon } from './types/cloudInternetGw';
import { cloudLoadBalancerIcon } from './types/cloudLoadBalancer';
import { cloudNatGwIcon } from './types/cloudNatGw';
import { cloudRouterIcon } from './types/cloudRouter';
import { cloudTransitHubIcon } from './types/cloudTransitHub';
import { cloudVpnGwIcon } from './types/cloudVpnGw';
import { consoleServerIcon } from './types/consoleServer';
import { fexIcon } from './types/fex';
import { fwIcon } from './types/fw';
import { gcpCloudNatIcon } from './types/gcpCloudNat';
import { gcpInstanceIcon } from './types/gcpInstance';
import { gcpLbIcon } from './types/gcpLb';
import { gcpNicIcon } from './types/gcpNic';
import { gcpRouterIcon } from './types/gcpRouter';
import { gcpServiceConnectIcon } from './types/gcpServiceConnect';
import { gcpSubnetIcon } from './types/gcpSubnet';
import { gcpVpcIcon } from './types/gcpVpc';
import { gcpVpcPeeringIcon } from './types/gcpVpcPeering';
import { hostIcon } from './types/host';
import { l3SwitchIcon } from './types/l3switch';
import { lbIcon } from './types/lb';
import { networkManagementIcon } from './types/networkManagement';
import { nx7000Icon } from './types/nx7000';
import { phoneIcon } from './types/phone';
import { routerIcon } from './types/router';
import { securityManagementIcon } from './types/securityManagement';
import { serverIcon } from './types/server';
import { switchIcon } from './types/switch';
import { transitIcon } from './types/transit';
import { unknownIcon } from './types/unknown';
import { vgwIcon } from './types/vgw';
import { waasIcon } from './types/waas';
import { wlcIcon } from './types/wlc';

export const nodeTypeIcons: { [key in NodeType]: NodeTypeIcon } = {
  aciLeaf: aciLeafIcon,
  aciSpine: aciSpineIcon,
  ap: apIcon,
  apic: apicIcon,
  awsAlb: awsAlbIcon,
  awsCne: awsCneIcon,
  awsDxgw: awsDxgwIcon,
  awsEni: awsEniIcon,
  awsIgw: awsIgwIcon,
  awsInstance: awsInstanceIcon,
  awsNatGw: awsNatGwIcon,
  awsNlb: awsNlbIcon,
  awsSubnet: awsSubnetIcon,
  awsTgw: awsTgwIcon,
  awsVpc: awsVpcIcon,
  awsVpcPeering: awsVpcPeeringIcon,
  awsVgw: awsVgwIcon,
  azureAppGw: azureAppGwIcon,
  azureErc: azureErcIcon,
  azureFw: azureFwIcon,
  azureGw: azureGwIcon,
  azureInstance: azureInstanceIcon,
  azureLb: azureLbIcon,
  azureNat: azureNatIcon,
  azureNic: azureNicIcon,
  azureSubnet: azureSubnetIcon,
  azureVhub: azureVhubIcon,
  azureVnet: azureVnetIcon,
  azureVnetPeering: azureVnetPeeringIcon,
  cloud: cloudIcon,
  cloudInstance: cloudInstanceIcon,
  cloudInternetGw: cloudInternetGwIcon,
  cloudLoadBalancer: cloudLoadBalancerIcon,
  cloudNatGw: cloudNatGwIcon,
  cloudRouter: cloudRouterIcon,
  cloudTransitHub: cloudTransitHubIcon,
  cloudVpnGw: cloudVpnGwIcon,
  consoleServer: consoleServerIcon,
  fex: fexIcon,
  fw: fwIcon,
  gcpInstance: gcpInstanceIcon,
  gcpLb: gcpLbIcon,
  gcpCloudNat: gcpCloudNatIcon,
  gcpNic: gcpNicIcon,
  gcpServiceConnect: gcpServiceConnectIcon,
  gcpRouter: gcpRouterIcon,
  gcpSubnet: gcpSubnetIcon,
  gcpVpc: gcpVpcIcon,
  gcpVpcPeering: gcpVpcPeeringIcon,
  host: hostIcon,
  hostGroup: hostIcon,
  l3switch: l3SwitchIcon,
  lb: lbIcon,
  networkManagement: networkManagementIcon,
  nx7000: nx7000Icon,
  phone: phoneIcon,
  phoneGroup: phoneIcon,
  router: routerIcon,
  securityManagement: securityManagementIcon,
  server: serverIcon,
  switch: switchIcon,
  transit: transitIcon,
  unknown: unknownIcon,
  vgw: vgwIcon,
  waas: waasIcon,
  wlc: wlcIcon,
  // NIM-21654 @see deviceTypes
  // awsGwlb: awsGwlbIcon,
  // awsGwVpce: awsGwVpceIcon,
  // awsPrivateLink: awsPrivateLinkIcon,
  // awsPrivateLinkEndpoint: awsPrivateLinkEndpointIcon,
};

export function getNodeDataHitArea(nodeData: NodeData): HitArea {
  return moveHitArea(
    nodeTypeIcons[nodeData.type].getHitArea(nodeData.style),
    nodeData.position,
  );
}

export function getNodeDataClippingPoint(
  nodeData: NodeData,
): (direction: NonZeroVector) => Point {
  const getClippingPoint = nodeTypeIcons[nodeData.type].makeGetClippingPoint(
    nodeData.style,
  );
  const { position } = nodeData;
  return (direction) =>
    point.createOnVector(getClippingPoint(direction), position);
}
